/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'
import {
    Box,
    Button,
    Accordion,
    AccordionButton,
    AccordionItem,
    AccordionPanel,
    useMultiStyleConfig,
    Stack,
    Flex,
    Text
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useForm} from 'react-hook-form'
import PromoCodeFields from '../forms/promo-code-fields'
import {API_ERROR_MESSAGE} from '@salesforce/retail-react-app/app/constants'
import {useShopperBasketsMutation} from '@salesforce/commerce-sdk-react'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {ChevronDownIcon, AddIcon} from '../icons'
import {useCustomToast} from '../../contexts/custom-toast-context'

import {sendViewErrorEvent} from '../../utils/gtm-utils'

export const usePromoCode = () => {
    const {formatMessage} = useIntl()
    const {data: basket} = useCurrentBasket()
    const form = useForm()
    const {showCustomToast} = useCustomToast()

    const applyPromoCodeMutation = useShopperBasketsMutation('addCouponToBasket')
    const removePromoCodeMutation = useShopperBasketsMutation('removeCouponFromBasket')

    const submitPromoCode = async ({code}) => {
        try {
            await applyPromoCodeMutation.mutateAsync(
                {
                    parameters: {basketId: basket?.basketId},
                    body: {
                        code
                    }
                },
                {
                    onSuccess: (data) => {
                        const {couponItems} = data
                        const invalidCoupons = couponItems.filter(
                            (item) => item.statusCode !== 'applied'
                        )

                        if (invalidCoupons.length > 0) {
                            invalidCoupons.forEach((coupon) =>
                                autoRemovePromoCode(basket?.basketId, coupon?.couponItemId)
                            )
                        }
                    }
                }
            )

            form.reset({code: ''})
        } catch (e) {
            // GTM Modification
            sendViewErrorEvent('Enter a valid promo code')
            form.setError(
                'code',
                {
                    type: 'manual',
                    message: formatMessage({
                        defaultMessage: 'Enter a valid promo code',
                        id: 'use_promocode.error.invalid_promo_code'
                    })
                },
                {shouldFocus: true}
            )
        }
    }

    const autoRemovePromoCode = async (basketId, couponItemId) => {
        removePromoCodeMutation.mutate(
            {
                parameters: {basketId: basketId, couponItemId}
            },
            {
                onSettled: () => {
                    // GTM Modification
                    sendViewErrorEvent('Promotion cannot be applied')
                    form.reset({code: ''})
                    form.setError(
                        'code',
                        {
                            type: 'manual',
                            message: formatMessage({
                                defaultMessage: 'Promotion cannot be applied',
                                id: 'use_promocode.error.promotion_cannot_be_applied'
                            })
                        },
                        {shouldFocus: true}
                    )
                }
            }
        )
    }

    const removePromoCode = async (couponItemId) => {
        removePromoCodeMutation.mutate(
            {
                parameters: {basketId: basket?.basketId, couponItemId}
            },
            {
                onSuccess: () => {
                    showCustomToast({
                        message: formatMessage({
                            defaultMessage: 'Promotion removed',
                            id: 'use_promocode.info.promo_removed'
                        })
                    })
                },
                onError: () => {
                    showCustomToast({
                        message: formatMessage(API_ERROR_MESSAGE)
                    })
                }
            }
        )
    }

    useEffect(() => {
        if (basket?.couponItems?.length > 0) {
            basket.couponItems
                .filter((item) => item.statusCode !== 'applied')
                .forEach((item) => {
                    autoRemovePromoCode(basket.basketId, item.couponItemId)
                })
        }
    }, [basket])

    return {form, submitPromoCode, removePromoCode}
}

export const PromoCode = ({itemProps, basket}) => {
    const [isOpen, setOpen] = useState(false)
    const [showAddForm, setShowAddForm] = useState(false)
    const {removePromoCode, form, submitPromoCode} = usePromoCode()
    const styles = useMultiStyleConfig('PromoCode')

    const appliedCoupons = React.useMemo(() => {
        return (basket?.couponItems || []).filter(
            (item) =>
                item.statusCode === 'applied' &&
                item?.couponItemId !== basket?.c_sflmLoyaltyCouponID
        )
    }, [basket?.couponItems, basket?.c_sflmLoyaltyCouponID])

    const appliedCount = appliedCoupons.length

    // Create a mapping of couponItemId to promotionName
    const promotionNamesMap = React.useMemo(() => {
        const map = {}

        // Collect order-level promotions
        basket?.orderPriceAdjustments?.forEach((adjustment) => {
            const matchingCoupon = basket?.couponItems?.find(
                (item) => item.code === adjustment.couponCode
            )
            if (matchingCoupon) {
                map[matchingCoupon.couponItemId] = adjustment.c_calloutMsg || adjustment.itemText
            }
        })

        // Collect product-level promotions
        basket?.productItems?.forEach((productItem) => {
            productItem?.priceAdjustments?.forEach((adjustment) => {
                const matchingCoupon = basket?.couponItems?.find(
                    (item) => item.code === adjustment.couponCode
                )
                if (matchingCoupon && !map[matchingCoupon.couponItemId]) {
                    map[matchingCoupon.couponItemId] =
                        adjustment.c_calloutMsg || adjustment.itemText
                }
            })
        })

        return map
    }, [basket?.orderPriceAdjustments, basket?.productItems, basket?.couponItems])

    const prevAppliedCountRef = React.useRef(appliedCount)

    // Hide "add another" form only when a new coupon is genuinely applied
    useEffect(() => {
        if (appliedCount > prevAppliedCountRef.current) {
            setOpen(true)
            setShowAddForm(false)
        }
        if (appliedCount === 0) {
            setShowAddForm(false)
        }
        prevAppliedCountRef.current = appliedCount
    }, [appliedCount])

    const handleRemove = (couponItemId) => {
        removePromoCode(couponItemId)
    }

    const showInputForm = appliedCount === 0 || showAddForm

    return (
        <Accordion
            allowToggle
            index={isOpen ? 0 : -1}
            onChange={() => {
                setOpen(!isOpen)
                if (isOpen) {
                    setShowAddForm(false)
                }
            }}
        >
            <AccordionItem {...styles.accordionItem} {...itemProps}>
                {({isExpanded}) => (
                    <>
                        <AccordionButton
                            data-testid="promo-code-accordion-button"
                            {...styles.accordionButton}
                            onClick={() => form.reset()}
                        >
                            <Text {...styles.accordionButtonText}>
                                {appliedCount > 0 ? (
                                    <>
                                        <FormattedMessage
                                            defaultMessage="Do you have a promotional code?"
                                            id="promo_code.heading.do_you_have"
                                        />
                                        {` (${appliedCount})`}
                                    </>
                                ) : (
                                    <FormattedMessage
                                        defaultMessage="Promo Code"
                                        id="promo_code.heading.promo_code"
                                    />
                                )}
                            </Text>
                            <ChevronDownIcon
                                {...styles.accordionArrow}
                                {...(isExpanded && styles.accordionArrowExpanded)}
                            />
                        </AccordionButton>

                        <AccordionPanel {...styles.accordionPanel}>
                            {appliedCount > 0 && (
                                <Stack {...styles.appliedCouponsStack}>
                                    {appliedCoupons.map((item) => (
                                        <Box
                                            key={item.couponItemId}
                                            {...styles.couponCard}
                                        >
                                            <Flex {...styles.couponCardFlex}>
                                                {/* data-private: the applied coupon (code + promo name) is redacted from LogRocket replays */}
                                                <Box data-private>
                                                    <Text {...styles.couponCode}>
                                                        {item.code}
                                                    </Text>
                                                    {promotionNamesMap[
                                                        item.couponItemId
                                                    ] && (
                                                        <Text {...styles.couponPromoName}>
                                                            {
                                                                promotionNamesMap[
                                                                    item.couponItemId
                                                                ]
                                                            }
                                                        </Text>
                                                    )}
                                                </Box>
                                                {!basket.orderNo && (
                                                    <Button
                                                        {...styles.removeButton}
                                                        onClick={() =>
                                                            handleRemove(
                                                                item.couponItemId
                                                            )
                                                        }
                                                    >
                                                        <Text as="span" {...styles.removeButton}>
                                                            <FormattedMessage
                                                                defaultMessage="Remove"
                                                                id="promo_code.action.remove"
                                                            />
                                                        </Text>
                                                    </Button>
                                                )}
                                            </Flex>
                                        </Box>
                                    ))}
                                </Stack>
                            )}

                            {showInputForm && (
                                <Box
                                    data-testid="promo-code-form"
                                    as="form"
                                    onSubmit={form.handleSubmit(submitPromoCode)}
                                >
                                    <PromoCodeFields form={form} />
                                </Box>
                            )}

                            {appliedCount > 0 && !showAddForm && (
                                <Button
                                    {...styles.addAnotherButton}
                                    onClick={() => {
                                        form.reset({code: ''})
                                        setShowAddForm(true)
                                    }}
                                    leftIcon={<AddIcon {...styles.addAnotherIcon} />}
                                >
                                    <Text as='span' {...styles.addAnotherButton}>
                                        <FormattedMessage
                                            defaultMessage="Add another promotional code"
                                            id="promo_code.action.add_another"
                                        />
                                    </Text>
                                </Button>
                            )}
                        </AccordionPanel>
                    </>
                )}
            </AccordionItem>
        </Accordion>
    )
}

PromoCode.propTypes = {
    /** Props applied to inner AccordionItem. Useful for style overrides. */
    itemProps: PropTypes.object,
    basket: PropTypes.object
}

export default PromoCode
